// Single source of truth for site content.
// Edit text, contact details and equipment here — components read from this file,
// so most content updates never require touching layout code.

import supertec800 from "../assets/media/supertec-lathe-800.webp";
import supertec1600 from "../assets/media/supertec-lathe-1600.webp";
import vmx24800 from "../assets/media/hurco-vmx24-800.webp";
import vmx241600 from "../assets/media/hurco-vmx24-1600.webp";
import hurcoVmc800 from "../assets/media/hurco-vmc-800.webp";
import hurcoVmc1600 from "../assets/media/hurco-vmc-1600.webp";
import interior800 from "../assets/media/turning-center-interior-800.webp";
import interior1600 from "../assets/media/turning-center-interior-1600.webp";
import manual800 from "../assets/media/manual-milling-800.webp";
import manual1600 from "../assets/media/manual-milling-1600.webp";

import clipTurning from "../assets/media/clip-turning.mp4";
import clipTurningPoster from "../assets/media/clip-turning-poster.jpg";
import clipCoolant from "../assets/media/clip-coolant.mp4";
import clipCoolantPoster from "../assets/media/clip-coolant-poster.jpg";
import clipMilling from "../assets/media/clip-milling.mp4";
import clipMillingPoster from "../assets/media/clip-milling-poster.jpg";

export const company = {
	name: "B & H Machining",
	legalName: "B & H Machining Inc.",
	phone: "(650) 637-1171",
	phoneHref: "tel:+16506371171",
	email: "horng534@gmail.com",
	street: "1020 Terminal Way",
	city: "San Carlos",
	region: "CA",
	postal: "94070",
	mapUrl: "https://maps.app.goo.gl/8HnHU1xQxteUCy1T9",
	url: "https://bh-machining.com",
};

// Equipment showcase. `span` controls the width of the plate on the 12-column grid
// (desktop only — everything stacks on phones).
// NOTE: machine names were read from the badges in the photos. Confirm exact models with B&H.
export const equipment = [
	{
		kind: "image",
		name: "Supertec CNC turning center",
		note: "Tubes, rings, insulators and other round parts, turned to print.",
		src: supertec1600,
		srcSmall: supertec800,
		alt: "Supertec CNC lathe on the B&H shop floor",
		span: 8,
		ratio: "4 / 3",
	},
	{
		kind: "video",
		name: "Turning under flood coolant",
		note: "Coolant keeps the tool and the part cool through every pass.",
		src: clipTurning,
		poster: clipTurningPoster,
		alt: "Close-up of a part being turned with coolant spraying",
		span: 4,
		ratio: "9 / 16",
	},
	{
		kind: "video",
		name: "Hurco VMX24 at work",
		note: "Three-axis milling of plates, housings and fixtures.",
		src: clipMilling,
		poster: clipMillingPoster,
		alt: "Hurco spindle milling a part behind the machine window",
		span: 4,
		ratio: "9 / 16",
	},
	{
		kind: "image",
		name: "Hurco VMX24 vertical machining center",
		note: "Repeatable CNC milling for prototypes and production lots.",
		src: vmx241600,
		srcSmall: vmx24800,
		alt: "Hurco VMX24 vertical machining center",
		span: 8,
		ratio: "4 / 3",
	},
	{
		kind: "image",
		name: "Hurco vertical machining center",
		note: "A second machining center for added capacity and shorter lead times.",
		src: hurcoVmc1600,
		srcSmall: hurcoVmc800,
		alt: "Black Hurco vertical machining center with tooling cart",
		span: 6,
		ratio: "4 / 3",
	},
	{
		kind: "image",
		name: "Inside the turning center",
		note: "Turret tooling and adjustable coolant lines.",
		src: interior1600,
		srcSmall: interior800,
		alt: "Interior of a CNC turning center showing the chuck and tool turret",
		span: 3,
		ratio: "3 / 4",
	},
	{
		kind: "video",
		name: "Finishing pass",
		note: "Light cuts bring parts to final size.",
		src: clipCoolant,
		poster: clipCoolantPoster,
		alt: "Coolant spray during a finishing pass on the lathe",
		span: 3,
		ratio: "3 / 4",
	},
	{
		kind: "image",
		name: "Manual milling",
		note: "Quick setups for prototypes, one-offs and rework.",
		src: manual1600,
		srcSmall: manual800,
		alt: "Machinist setting up a part in a fixture on a manual mill",
		span: 12,
		ratio: "21 / 9",
	},
];

export const capabilities = [
	{
		name: "Diamond grinding",
		text: "Surface, OD and ID grinding with diamond abrasive wheels for hard, brittle materials like alumina, quartz and glass.",
	},
	{
		name: "Core drilling",
		text: "Precise holes and cored blanks in ceramic, quartz and glass, from small bores to large diameters.",
	},
	{
		name: "CNC milling",
		text: "Three-axis machining on Hurco vertical machining centers for pockets, profiles and complex features.",
	},
	{
		name: "CNC turning",
		text: "Turned tubes, rings, shafts and insulators on our Supertec turning center.",
	},
	{
		name: "Lapping and polishing",
		text: "Flat, smooth surfaces and fine finishes for sealing faces, wafers and optical parts.",
	},
	{
		name: "Sawing, honing and tapping",
		text: "Secondary operations handled in-house, so parts don't leave the shop between steps.",
	},
];

export const materials = [
	{
		name: "Aluminum oxide (Al₂O₃)",
		text: "Extremely resistant to wear and corrosion, with very high electrical insulation.",
	},
	{
		name: "Quartz",
		text: "High purity and thermal stability for semiconductor and lab components.",
	},
	{
		name: "Glass",
		text: "Machined to your exact specifications, small and large.",
	},
	{
		name: "Metals",
		text: "Metal components turned and milled on our CNC equipment.",
	},
];

export const industries = [
	{ name: "Semiconductor", text: "Insulators, rings and fixtures for process equipment." },
	{ name: "Medical devices", text: "Precision ceramic components for diagnostic and treatment systems." },
	{ name: "Aerospace", text: "Wear-resistant parts that hold tolerance in demanding environments." },
	{ name: "Electronics", text: "Electrically insulating substrates, spacers and housings." },
];

export const testimonials = [
	{ quote: "Excellent quality and delivery performance.", source: "Agilent Technologies, Inc." },
	{ quote: "Priority on future procurement of certified parts.", source: "Varian Medical Systems" },
];
